import { Component, useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowRight, Home, RefreshCw, Sparkles } from 'lucide-react';
import { isChunkLoadError, isUpdateReady, onUpdateReady, recoverByReload } from '@/lib/app-update';
import { reportClientError } from '@/lib/client-errors';

/**
 * حدودُ خطأ الشاشات (29 سبتمبر 2026) — انظر `lib/app-update.ts`. بلا حدودٍ يُفرغ React الشجرةَ كلَّها
 * عند أول خطأٍ في التصيير أو في تحميل شاشة، فيرى المستخدمُ خلفيةً بلا شيء ولا سبيلَ للعودة (التطبيقُ
 * المثبَّت بلا أزرار متصفّح). هنا: فشلُ تحميل الشاشة ← إعادةُ تحميلٍ مرّةً؛ وما عداه ← بطاقةٌ بثلاثة
 * أزرار تعمل بلا React Router (فالخطأُ قد يكون فيه): تحديثٌ، رجوعٌ، الرئيسية.
 *
 * والمفتاحُ هو المسار: تغيّرُ المسار (رجوعٌ بزرّ المتصفّح مثلاً) يُعيد بناءَ الحدود فتُعرض الشاشةُ
 * الجديدة لا البطاقةُ القديمة.
 */
interface State { error: Error | null }

class Boundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State { return { error }; }

  componentDidCatch(error: Error): void {
    // البلاغُ أولاً — وقبل إعادة التحميل، فالإعادةُ تمحو كلَّ شيء
    reportClientError(isChunkLoadError(error) ? 'chunk' : 'render', error);
    if (isChunkLoadError(error) && recoverByReload(`شاشةٌ لم تُحمَّل: ${error.message}`)) return;
    console.error('[rafrafa] عطلٌ في عرض الشاشة', error);
  }

  render() {
    if (!this.state.error) return this.props.children;
    const chunk = isChunkLoadError(this.state.error);
    return (
      <div className="crash" role="alert" data-testid="crash-card">
        <div className="crash__card">
          <h1 className="crash__title">{chunk ? 'تعذّر تحميل هذه الشاشة' : 'تعذّر عرض هذه الشاشة'}</h1>
          <p className="crash__text">
            {chunk
              ? 'ليس خطأك: صدرت نسخةٌ جديدة من المنصة أو انقطع الاتصال أثناء التحميل. أعد تحميل الصفحة وسيعمل كلُّ شيء.'
              : 'ليس خطأك: حدث عطلٌ أثناء العرض. أعد تحميل الصفحة، أو عد إلى الشاشة السابقة.'}
          </p>
          <p className="crash__note caption">أُبلغ الفريقُ التقني بهذا العطل تلقائياً — بلا أيّ بياناتٍ شخصية.</p>
          <div className="crash__actions">
            <button type="button" className="btn btn--primary" onClick={() => window.location.reload()}><RefreshCw size={16} aria-hidden /> أعد تحميل الصفحة</button>
            <button type="button" className="btn btn--secondary" onClick={() => window.history.back()}><ArrowRight size={16} aria-hidden /> الرجوع</button>
            <a className="btn btn--ghost" href="/"><Home size={16} aria-hidden /> الصفحة الرئيسية</a>
          </div>
        </div>
      </div>
    );
  }
}

export function AppErrorBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return <Boundary key={pathname}>{children}</Boundary>;
}

/**
 * «صدر تحديث»: يظهر حين يتولّى عاملُ خدمةٍ جديد التبويبَ (نُشرت نسخة). التحديثُ بضغطةٍ لا تلقائياً —
 * قد يكون المستخدمُ يكتب نموذجاً. يُخفى بالضغط ويعود عند التبويب التالي.
 */
export function UpdateBanner() {
  const [ready, setReady] = useState(isUpdateReady);
  const [hidden, setHidden] = useState(false);
  useEffect(() => onUpdateReady(setReady), []);
  if (!ready || hidden) return null;
  return (
    <div className="update-banner" role="status" aria-live="polite" data-testid="update-banner">
      <Sparkles size={18} aria-hidden />
      <span className="grow">صدرت نسخةٌ جديدة من رفرفة — أعد تحميل الصفحة حتى تعمل كلُّ الشاشات.</span>
      <button type="button" className="btn btn--primary btn--sm" onClick={() => window.location.reload()}>أعد التحميل الآن</button>
      <button type="button" className="btn btn--ghost btn--sm" onClick={() => setHidden(true)}>لاحقاً</button>
    </div>
  );
}
